/**
 * PropertyDeleteModal — confirm перед cascade-remove determined property
 * (§13.5 / DM-28). Показывает `affected_row_count` из meta и честно говорит,
 * что source rows не удаляются.
 *
 * Block M1b (TZ 2026-07-29). State-driven через props (parent держит
 * `pending`).
 */
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';

import { Modal } from '@/components/ui/Modal';
import type { DeterminedPropertySummary } from './DeterminedPropertiesGrid';

export interface PropertyDeleteModalProps {
  property: DeterminedPropertySummary | null;
  pipelineId: string | null;
  onClose: () => void;
  onConfirm: () => void;
  busy: boolean;
}

export function PropertyDeleteModal({
  property,
  pipelineId,
  onClose,
  onConfirm,
  busy,
}: PropertyDeleteModalProps) {
  const { t } = useTranslation();

  return (
    <Modal
      open={Boolean(property)}
      onClose={onClose}
      title={t('process.matching.properties.deleteConfirm.title', {
        defaultValue: 'Remove property?',
      })}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary text-xs px-3 py-1.5"
            disabled={busy}
          >
            {t('process.matching.properties.deleteConfirm.cancel', {
              defaultValue: 'Cancel',
            })}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy || !property || !pipelineId}
            className="btn-danger text-xs px-3 py-1.5"
          >
            {t('process.matching.properties.deleteConfirm.confirm', {
              defaultValue: 'Remove',
            })}
          </button>
        </>
      }
    >
      {property && (
        <div className="flex items-start gap-3 py-1">
          <AlertTriangle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
          <div className="text-sm text-surface-200 leading-snug space-y-1">
            <p>
              {t('process.matching.properties.deleteConfirm.body', {
                defaultValue:
                  'Will clear "{name}" ({n} rows). Source rows are not deleted.',
                name: property.name,
                n: property.presence,
              })}
            </p>
            <p className="text-[11px] text-surface-500">
              {t('process.matching.properties.deleteConfirm.hint', {
                defaultValue:
                  'Only the matching-layer property payload is removed. You can re-run property extraction later.',
              })}
            </p>
          </div>
        </div>
      )}
    </Modal>
  );
}
